
import { Component, Model, Prop, Vue } from 'bits/vue'
import { uniqueId } from 'lodash-es'

export enum OzInputSizePreset {
  H32px = 'H32px',
  H40px = 'H40px',
}

/**
 * @displayName Oz Input
 */
// set inheritAttrs so the values of $attrs intended for the inner input element (e.g. required, type, placeholder) won't be set for the div wrapper
// Reference: https://vuejs.org/v2/guide/components-props.html#Disabling-Attribute-Inheritance
// In Vue 3 $attrs will include class and style so things may break: https://v3.vuejs.org/guide/migration/attrs-includes-class-style.html#overview
// Can be fixed by following this: https://github.com/vuejs/vue/issues/6144#issue-243557652

@Component({ inheritAttrs: false })
export default class OzInput extends Vue {
  $refs!: {
    inputElement: HTMLInputElement
  }

  SizePreset = OzInputSizePreset

  /**
   * Dark mode.
   */
  @Prop({ default: 'auto' }) darkMode!: true | false | 'auto'

  /**
   * Changes border color if there are any errors
   * @values yes, no, unknown
   */
  @Prop({ default: 'unknown' }) valid!: 'yes' | 'no' | 'unknown'

  /**
   * Determines if input is disabled
   * @default false
   */
  @Prop({ default: false }) disabled?: boolean

  /**
   * Used to query the input element for testing purposes
   */
  @Prop({ default: '' }) testId?: string

  /**
   * Display a message after validating the input value
   */
  @Prop({ default: '' }) validationMessage?: string

  /**
   * Text Alignment to be applied at the Input component
   */
  @Prop({ default: undefined }) textAlign!: 'left' | 'right' | 'center' | undefined

  /**
   * Size preset
   */
  @Prop({ default: OzInputSizePreset.H40px }) sizePreset!: OzInputSizePreset

  /**
   * Whether to show border or not
   */
  @Prop({ default: true }) xBorder!: boolean

  isFocused = false

  // this will be used if v-model is not passed to the component
  internalInputValue = ''
  validationMessageId = uniqueId('validationMessageId')

  /**
   * v-model will be binded to bindedInputValue and updated using the event update:modelValue
   * Note:
   *  - Don't add a prop named bindedInputValue or emit update:modelValue in this component to avoid conflict
   */
  @Model('update:modelValue') readonly bindedInputValue!: string

  get inputValue(): string {
    return this.bindedInputValue ?? this.internalInputValue
  }

  get isValid(): boolean {
    return this.valid !== 'no'
  }

  get xValidationMessage(): boolean {
    return !this.isValid && !!this.validationMessage?.length
  }

  get hasExtraContent(): boolean {
    return !!this.$slots.extraContent
  }

  get borderClasses(): string | Record<string, boolean> {
    if (!this.xBorder) return ''

    const commonClasses = {
      'border-1': this.sizePreset === this.SizePreset.H32px,
      'border-2': this.sizePreset === this.SizePreset.H40px,
      'border-solid': true,
    }

    if (!this.isValid) return { ...commonClasses, 'border-danger-100': true }

    if (this.disabled)
      return {
        ...commonClasses,
        'border-grey-300': this.darkMode === false,
        'border-white-disabled': this.darkMode === true,
        'border-grey-300 dark:border-white-disabled': this.darkMode === 'auto',
      }

    if (this.isFocused)
      return {
        ...commonClasses,
        'border-grape-500': this.darkMode === false,
        'border-canary-500': this.darkMode === true,
        'border-grape-500 dark:border-canary-500': this.darkMode === 'auto',
      }

    return {
      ...commonClasses,
      'border-dark-ui-100': this.darkMode === false,
      'border-light-ui-100': this.darkMode === true,
      'border-dark-ui-100 dark:border-light-ui-100': this.darkMode === 'auto',
    }
  }

  focusin(event: FocusEvent): void {
    this.isFocused = true
    /**
     * Emit the focusin event again after we are done with our internal handling
     * So the user of the component can listen to it if they want
     * @property {FocusEvent} event
     */
    this.$emit('focusin', event)
  }

  focusout(event: FocusEvent): void {
    this.isFocused = false
    /**
     * Emit the focusout event again after we are done with our internal handling
     * So the user of the component can listen to it if they want
     * @property {FocusEvent} event
     */
    this.$emit('focusout', event)
  }

  blur(event: FocusEvent): void {
    /**
     * Emit the blur event again after we are done with our internal handling
     * So the user of the component can listen to it if they want
     * @property {BlurEvent} event
     */
    this.$emit('blur', event)
  }

  /**
   * If we use v-model instead of @input, we don't get an update event on every keypress.
   * @see https://github.com/vuejs/vue/issues/9777#issuecomment-478831263
   */
  handleInput(event: InputEvent): void {
    this.$emit('input', event)

    const value = (event?.target as HTMLInputElement)?.value
    this.internalInputValue = value

    /**
     * Trigger to update value bound using v-model
     * @property {string} value
     */
    this.$emit('update:modelValue', value)
  }

  focusInput(): void {
    this.$refs.inputElement.focus()
  }

  selectInput(): void {
    this.$refs.inputElement.select()
  }

  blurInput(): void {
    this.$refs.inputElement.blur()
  }
}
